import { Divider, Grid } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useApiClientWrapper, useMarketplace, useMasterData } from 'hooks';
import { FunctionComponent, useEffect, useState } from 'react';
import { MarketplacePetTag, MarketplacePetTags } from '@spike/marketplace-model';
import Chips from 'components/Client/Chips';
import { Option } from '@spike/model';
import { useDispatch, useSelector } from 'react-redux';
import { getPetTagsThunk, MarketplaceStatus, saveMarketplaceThunk } from '@spike/marketplace-action';
import { RootState } from 'store';
import useNonInitialEffect from '@versiondos/hooks';
import { showError } from '@spike/notifications-action';
import { setTimeZone } from 'actions/login/LoginActions';

interface PetTagProps {
    selectedPositivePersonalities: Array<Option<string>>;
    selectedNegativePersonalities: Array<Option<string>>;
    onChangePositive: (selectedPositivePersonalities: Array<Option<string>>) => void;
    onChangeNegative: (selectedNegativePersonalities: Array<Option<string>>) => void;
    className?: string;
    disabled?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {},
        containerPersonality: {
            '& p': {
                fontSize: '16px'
            },
            '& svg': {
                fontSize: '16px',
                marginRight: '5px'
            }
        },
        divider: {
            'margin': '24px 0px',

            '& hr': {
                backgroundColor: '#D4D4D4'
            }
        }
    })
);

const PetTag: FunctionComponent<PetTagProps> = props => {
    const classes = useStyles();
    const marketplace = useMarketplace();
    const masterData = useMasterData();

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const loading = useSelector<RootState, boolean>(state => state.marketplace.loading);
    const status = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);
    const store = useSelector<RootState, MarketplacePetTags>(state => state.marketplace.petTags);

    const [positives, setPositives] = useState<Option<string>[]>([]);
    const [negatives, setNegatives] = useState<Option<string>[]>([]);

    const [marketplacePositives, setMarketplacePositives] = useState([...marketplace.petPositivePersonality]);
    const [marketplaceNegatives, setMarketplaceNegatives] = useState([...marketplace.petNegativePersonality]);

    const [newTagName, setNewTagName] = useState('');

    useEffect(() => {
        dispatch(getPetTagsThunk(apiClientWrapper));
    }, [dispatch]);

    useNonInitialEffect(() => {
        if (status === MarketplaceStatus.SaveSuccess) {
            setNewTagName('');
            dispatch(getPetTagsThunk(apiClientWrapper));
            setMarketplacePositives(marketplace.petPositivePersonality);
            setMarketplaceNegatives(marketplace.petNegativePersonality);
        }

        if (status === MarketplaceStatus.GetPetTagsSuccess) {
            setPositives(transformToOptions(sortedPetTags(store.positive)));
            setNegatives(transformToOptions(sortedPetTags(store.negative)));
        }
    }, [status]);

    const transformToOptions = (tags: MarketplacePetTag[]): Option<string>[] => {
        return tags.map(tag => {
            return {
                id: tag.id,
                name: tag.name
            };
        });
    };

    const sortedPetTags = (petTagList: MarketplacePetTag[]) => {
        const getSourceOrder = (source: string) => {
            return source === 'default' ? 0 : 1;
        };

        return [...petTagList].sort((a, b) => {
            const sourceOrderA = getSourceOrder(a.source);
            const sourceOrderB = getSourceOrder(b.source);

            if (sourceOrderA !== sourceOrderB) {
                return sourceOrderA - sourceOrderB;
            }

            return a.name.localeCompare(b.name);
        });
    };

    const areFieldsValid = (name: string, petTagsDefault: Option<string>[]) => {
        const nameLower = name.toLowerCase();
        const nameWithUnderscore = nameLower.replace(/'/g, '').replace(/ /g, '_');
        const nameWithHyphen = nameLower.replace(/'/g, '').replace(/ /g, '-');

        const tagExists = petTagsDefault.some(tag => {
            const tagIdLower = tag.id.toLowerCase();
            return tagIdLower === nameWithUnderscore || tagIdLower === nameWithHyphen;
        });

        return tagExists;
    };

    const addPetTagHandle = (petTagCategory: string, name: string) => {
        if (name.trim() === '') {
            return;
        }

        const isPositiveTab = petTagCategory === 'positive';
        const currentList = isPositiveTab ? marketplacePositives : marketplaceNegatives;
        const currentListAll = isPositiveTab ? positives : negatives;

        if (areFieldsValid(name, currentListAll)) {
            dispatch(showError('This personality is already added'));
            return;
        }

        const newTags = [...currentList, name];
        const newMarketplace = { ...marketplace };

        if (isPositiveTab) {
            newMarketplace.petPositivePersonality = [...newTags];
        } else {
            newMarketplace.petNegativePersonality = [...newTags];
        }

        dispatch(saveMarketplaceThunk(apiClientWrapper, newMarketplace, setTimeZone));
    };

    return (
        <Grid
            container
            item
            xs={12}
            className={props.className}
        >
            <Chips
                icon={<>👍</>}
                tags={positives}
                petTagCategory={'positive'}
                selectedTags={props.selectedPositivePersonalities}
                onChangeSelection={props.onChangePositive}
                disabled={props.disabled}
                showAddTagCustom={true}
                newPetTagName={newTagName}
                setNewPetTagName={setNewTagName}
                onSavePetTagCustom={addPetTagHandle}
            />
            <Grid
                item
                xs={12}
                className={classes.divider}
            >
                <Divider />
            </Grid>
            <Chips
                icon={<>👎</>}
                tags={negatives}
                petTagCategory={'negative'}
                selectedTags={props.selectedNegativePersonalities}
                onChangeSelection={props.onChangeNegative}
                disabled={props.disabled}
                showAddTagCustom={true}
                newPetTagName={newTagName}
                setNewPetTagName={setNewTagName}
                onSavePetTagCustom={addPetTagHandle}
            />
        </Grid>
    );
};

export default PetTag;
